import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import './GlobalPresence.css';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const locations = [
  { name: 'Chennai', country: 'India', coordinates: [80.27, 13.08], region: 'india' },
  { name: 'Bengaluru', country: 'India', coordinates: [77.59, 12.97], region: 'india' },
  { name: 'Pune', country: 'India', coordinates: [73.85, 18.52], region: 'india' },
  { name: 'Mumbai', country: 'India', coordinates: [72.87, 19.07], region: 'india' },
  { name: 'Coimbatore', country: 'India', coordinates: [76.97, 11.02], region: 'india' },
  { name: 'Trichy', country: 'India', coordinates: [78.69, 10.79], region: 'india' },
  { name: 'Singapore', country: 'Singapore', coordinates: [103.82, 1.35], region: 'sea' },
  { name: 'Kuala Lumpur', country: 'Malaysia', coordinates: [101.69, 3.14], region: 'sea' },
  { name: 'Seoul', country: 'South Korea', coordinates: [126.98, 37.57], region: 'asia' },
  { name: 'Taipei', country: 'Taiwan', coordinates: [121.56, 25.03], region: 'asia' },
  { name: 'Mexico City', country: 'Mexico', coordinates: [-99.13, 19.43], region: 'americas' },
  { name: 'Melbourne', country: 'Australia', coordinates: [144.96, -37.81], region: 'apac' },
];

const regionColors = {
  india: '#ff9500',
  sea: '#00c6ff',
  asia: '#a855f7',
  americas: '#30d158',
  apac: '#ff6b6b',
};

const stats = [
  { value: '7', label: 'Countries' },
  { value: '12', label: 'Cities' },
  { value: '3', label: 'Continents' },
  { value: '10+', label: 'Years Global' },
];

const GlobalPresence = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [tooltip, setTooltip] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.4, 0.25, 1] } },
  };

  return (
    <section id="global-presence" className="global-presence" ref={ref}>
      <div className="gp-container">
        {/* Header */}
        <motion.div
          className="gp-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.h2 className="gp-title" variants={itemVariants}>
            Delivering Excellence <span className="gp-highlight">Across the Globe</span>
          </motion.h2>

          <motion.p className="gp-subtitle" variants={itemVariants}>
            From industrial corridors in India to cutting-edge facilities in South Korea, Singapore,
            Taiwan, Malaysia, Mexico, and Australia — our automation expertise knows no borders.
          </motion.p>
        </motion.div>

        {/* Stats row */}
        <motion.div
          className="gp-stats"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {stats.map((s, i) => (
            <motion.div key={i} className="gp-stat" variants={itemVariants}>
              <span className="gp-stat-value">{s.value}</span>
              <span className="gp-stat-label">{s.label}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Map */}
        <motion.div
          className="gp-map-wrapper"
          variants={itemVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <div className="gp-map-card">
            {tooltip && (
              <div className="gp-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                <strong>{tooltip.name}</strong>
                <span>{tooltip.country}</span>
              </div>
            )}
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 140, center: [60, 15] }}
              width={960}
              height={480}
              style={{ width: '100%', height: 'auto', borderRadius: '18px' }}
            >
              <ZoomableGroup>
                {/* Ocean background */}
                <rect x="0" y="0" width="960" height="480" fill="rgba(180, 215, 245, 0.88)" />
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#c8dbe8"
                        stroke="#ffffff"
                        strokeWidth={0.6}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: '#a8c5d8', outline: 'none' },
                          pressed: { outline: 'none' },
                        }}
                      />
                    ))
                  }
                </Geographies>

                {locations.map((loc, i) => (
                  <Marker
                    key={i}
                    coordinates={loc.coordinates}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                      const mapRect = e.currentTarget.closest('.gp-map-card').getBoundingClientRect();
                      setTooltip({
                        name: loc.name,
                        country: loc.country,
                        x: e.clientX - mapRect.left + 10,
                        y: e.clientY - mapRect.top - 40,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    {/* Outer pulse ring */}
                    <circle r={9} fill={regionColors[loc.region]} opacity={0.2}>
                      <animate attributeName="r" from="7" to="14" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.25" to="0" dur="2s" repeatCount="indefinite" />
                    </circle>
                    {/* Inner dot */}
                    <circle
                      r={5}
                      fill={regionColors[loc.region]}
                      stroke="#ffffff"
                      strokeWidth={1.5}
                      style={{ cursor: 'pointer', filter: `drop-shadow(0 0 4px ${regionColors[loc.region]})` }}
                    />
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
          </div>

          {/* Legend */}
          <div className="gp-legend">
            {[
              { region: 'india', label: 'India (6 cities)' },
              { region: 'sea', label: 'SE Asia' },
              { region: 'asia', label: 'East Asia' },
              { region: 'americas', label: 'Americas' },
              { region: 'apac', label: 'Pacific' },
            ].map((l) => (
              <div key={l.region} className="gp-legend-item">
                <span className="gp-legend-dot" style={{ background: regionColors[l.region] }} />
                <span>{l.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalPresence;
